import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import candidateAuthService from '../../services/candidateAuth';
import { Candidate } from '../../types';

interface CandidateLoginProps {
  onAuthenticated: (candidate: Candidate) => void;
}

const CandidateLogin: React.FC<CandidateLoginProps> = ({ onAuthenticated }) => {
  const { nameSlug } = useParams<{ nameSlug: string }>();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCandidate = useCallback(async () => {
    if (!nameSlug) return;

    try {
      setInitialLoading(true);
      const response = await candidateAuthService.getCandidateByNameSlug(nameSlug);

      if (response.success && response.candidate) {
        setCandidate(response.candidate);
      } else {
        setError('Candidate profile not found');
      }
    } catch (err) {
      setError('Failed to load candidate profile');
    } finally {
      setInitialLoading(false);
    }
  }, [nameSlug]);

  useEffect(() => {
    if (nameSlug) {
      loadCandidate();
    }
  }, [nameSlug, loadCandidate]);


  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !passcode) return;

    setLoading(true);
    setError(null);

    try {
      const response = await candidateAuthService.authenticateCandidate(email, passcode);

      if (response.success && response.candidate) {
        onAuthenticated(response.candidate);
      } else {
        setError(response.error || 'Invalid credentials');
      }
    } catch (err) {
      setError('Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #0a0e27 0%, #1a1b3a 25%, #2a2d47 50%, #1e2139 75%, #0f1419 100%)'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!candidate) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #0a0e27 0%, #1a1b3a 25%, #2a2d47 50%, #1e2139 75%, #0f1419 100%)'
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%', mx: 2 }}>
          <CardContent sx={{ textAlign: 'center', p: 4 }}>
            <PersonIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Candidate Profile Not Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The requested candidate profile could not be found.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{
        background: 'linear-gradient(135deg, #0a0e27 0%, #1a1b3a 25%, #2a2d47 50%, #1e2139 75%, #0f1419 100%)',
        p: 2
      }}
    >
      <Card sx={{ maxWidth: 450, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Secure Access Required
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please enter your email address and access passcode to view your interview progress
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              disabled={loading}
              required
            />

            <TextField
              fullWidth
              label="Access Passcode"
              type={showPasscode ? 'text' : 'password'}
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              margin="normal"
              disabled={loading}
              required
              helperText="Enter the access passcode provided by M4 Consulting"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle passcode visibility"
                      onClick={() => setShowPasscode(!showPasscode)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPasscode ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading || !email || !passcode}
            >
              {loading ? <CircularProgress size={24} /> : 'Access Profile'}
            </Button>

            <Typography variant="body2" color="text.secondary" textAlign="center">
              Having trouble accessing your profile? Contact your M4 Consulting representative.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CandidateLogin;